import { useNotificationProvider as useAntdNotificationProvider } from "@refinedev/antd";
import { NotificationProvider } from "@refinedev/core";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

/**
 * Translate HTTP error messages from axios to user-friendly messages.
 */
const translateErrorMessage = (
  message: string | undefined,
  t: TFunction,
): string => {
  if (!message) return t("notifications.error");

  // Check for axios "Request failed with status code XXX" pattern
  const statusMatch = message.match(/Request failed with status code (\d+)/);
  if (statusMatch) {
    const statusCode = statusMatch[1];
    const translationKey = `notifications.httpError.${statusCode}`;
    const translated = t(translationKey, { defaultValue: "" });
    if (translated) return translated;
    return t("notifications.httpError.default", { defaultValue: message });
  }

  // Check for network error
  if (message.includes("Network Error") || message.includes("ERR_NETWORK")) {
    return t("notifications.networkError");
  }

  // Check for timeout
  if (message.includes("timeout") || message.includes("ETIMEDOUT")) {
    return t("notifications.httpError.408");
  }

  return message;
};

/**
 * Custom notification provider that wraps Antd's notification provider
 * and filters out auth-related notifications (login errors).
 * Also translates HTTP error messages to user-friendly Vietnamese messages.
 */
export const useCustomNotificationProvider = (): NotificationProvider => {
  const antdProvider = useAntdNotificationProvider();
  const { t } = useTranslation();

  return {
    open: (params) => {
      // Filter out login error notifications - these are handled in the Login form
      if (
        params.type === "error" &&
        (params.key === "login-error" ||
          params.message === "Login Error" ||
          params.description === "Invalid credentials")
      ) {
        return;
      }

      // Translate error messages for error notifications
      if (params.type === "error") {
        const translatedMessage = translateErrorMessage(
          params.message as string | undefined,
          t,
        );
        antdProvider.open({
          ...params,
          message: translatedMessage,
        });
        return;
      }

      antdProvider.open(params);
    },
    close: (key) => {
      antdProvider.close(key);
    },
  };
};
